import React, { useState } from 'react';
import { Box, Paper, Typography, Collapse, IconButton, LinearProgress, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { ProgressEvent } from '../types/progress';

interface ThinkingPanelProps {
  events: ProgressEvent[];
  isActive?: boolean;
}

const ThinkingPanel: React.FC<ThinkingPanelProps> = ({ events, isActive = true }) => {
  const [expanded, setExpanded] = useState(true);

  if (events.length === 0 && !isActive) {
    return null;
  }

  const renderEvent = (event: ProgressEvent, idx: number) => {
    const { type, data } = event;

    switch (type) {
      case 'search_start':
        return (
          <Box key={idx} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              🔍 Starting search across {data.sources?.length || 0} databases...
            </Typography>
          </Box>
        );

      case 'source_start':
        return (
          <Box key={idx} sx={{ mb: 0.5, ml: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • Searching {data.source}...
            </Typography>
          </Box>
        );

      case 'source_complete':
        return (
          <Box key={idx} sx={{ mb: 0.5, ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="success.main">
              ✓ {data.source}: {data.count} papers
            </Typography>
            <Chip
              label={`${data.time_ms}ms`}
              size="small"
              sx={{ height: 18, fontSize: '0.7rem' }}
            />
          </Box>
        );

      case 'deduplication_start':
        return (
          <Box key={idx} sx={{ mb: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              🔬 Deduplicating {data.total_papers} papers...
            </Typography>
          </Box>
        );

      case 'deduplication_complete':
        return (
          <Box key={idx} sx={{ mb: 0.5, ml: 2 }}>
            <Typography variant="body2" color="success.main">
              ✓ Removed {data.duplicates_removed} duplicates → {data.total_after} unique papers
            </Typography>
          </Box>
        );

      case 'theme_extraction_start':
        return (
          <Box key={idx} sx={{ mb: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              📊 Extracting themes from {data.paper_count} papers...
            </Typography>
          </Box>
        );

      case 'theme_extraction_complete':
        return (
          <Box key={idx} sx={{ mb: 1, ml: 2 }}>
            <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
              ✓ Identified {data.total_themes} themes
            </Typography>
            {data.themes && data.themes.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {data.themes.slice(0, 5).map((theme: any, i: number) => (
                  <Chip
                    key={i}
                    label={typeof theme === 'string' ? theme : theme.theme || 'Unknown'}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );

      case 'ranking_start':
        return (
          <Box key={idx} sx={{ mb: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              🎯 Ranking {data.paper_count} papers by relevance...
            </Typography>
          </Box>
        );

      case 'ranking_complete':
        return (
          <Box key={idx} sx={{ mb: 0.5, ml: 2 }}>
            <Typography variant="body2" color="success.main">
              ✓ Papers ranked and ready
            </Typography>
          </Box>
        );

      case 'thinking':
        return (
          <Box key={idx} sx={{ mb: 0.5, mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
              💭 {data.text}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
          🧠 Research Progress {isActive && events.length > 0 && '(In Progress)'}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ ml: 1 }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {isActive && events.length === 0 && (
        <Box sx={{ py: 1 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Initializing search...
          </Typography>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box
          sx={{
            maxHeight: 500,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
            },
          }}
        >
          {events.map((event, idx) => renderEvent(event, idx))}

          {isActive && events.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ThinkingPanel;
